import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router, UrlSerializer } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { map, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { BackofficeUsersRequestModel } from '../../../helpers/objects/request/backoffice/backoffice-users/backoffice-users-request-model';
import { BackofficeUsersResponseModel } from '../../../helpers/objects/response/backoffice-users/backoffice-users-response-model';
import { BackofficeUsersSetupModel } from '../../../helpers/objects/dto/backoffice/backoffice-users/backoffice-users-setup/backoffice-users-setup-model';
import { BackofficeUsersModel } from '../../../helpers/objects/dto/backoffice/backoffice-users/backoffice-users-model';
import { BackofficeAdhesionRequestsRequestModel } from '../../../helpers/objects/request/backoffice/backoffice-adhesion-requests/backoffice-adhesion-requests-model';
import { BackofficeAdhesionRequestResponseModel } from '../../../helpers/objects/response/backoffice-adhesion-request-response-model/backoffice-adhesion-request-response-model';

@Injectable({
  providedIn: 'root'
})
export class BackofficeUsersService {
  appConfig: EnvironmentModel = new EnvironmentModel();
  usersSetup: BackofficeUsersSetupModel = new BackofficeUsersSetupModel();

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel,
    private serializer: UrlSerializer,
    private translateService: TranslateService,
    private router: Router
  ) {
    this.appConfig = appConfigTemp;
  }

  getUsersSetup(): Observable<BackofficeUsersSetupModel> {
    if (this.usersSetup.profiles && this.usersSetup.profiles.length > 0) {
      // Return it as an observable if this.usersSetup.profiles already has the profiles
      return of(this.usersSetup);
    }
    return this.http.get<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId + '/setup').pipe(
      map((response) => {
        this.usersSetup = response.body as BackofficeUsersSetupModel;
        return this.usersSetup as BackofficeUsersSetupModel;
      })
    );
  }

  getUsers(filter: BackofficeUsersRequestModel): Observable<BackofficeUsersResponseModel> {
    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });
    return this.getUsersSetup().pipe(
      switchMap((userSetup) => {
        if (Array.isArray(userSetup.profiles)) {
          return this.http.get<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId, { params: httpParams }).pipe(
            map((response) => {
              response.body.userList.forEach((person: any) => {
                userSetup.profiles.forEach((profile: any) => {
                  if (person.profileDescription === profile) {
                    // api is not working
                  }
                });
              });
              return response.body as BackofficeUsersResponseModel;
            })
          );
        } else {
          throw new Error('userSetup is not an array');
        }
      })
    );
  }

  getUserDetail(userId: string): Observable<BackofficeUsersModel> {
    return this.http.get<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId + '/detail/' + userId).pipe(
      map((response) => {
        return response.body.user as BackofficeUsersModel;
      })
    );
  }

  editUserDetail(data: BackofficeUsersModel): Observable<any> {
    return this.http.put<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId, data).pipe(map((response) => response.body));
  }

  addUserBackoffice(data: BackofficeUsersModel): Observable<any> {
    return this.http.post<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId, data).pipe(map((response) => response.body));
  }

  download(filter: any): string {
    const extraParams = {
      pageSize: 10000,
      page: 1,
      Authorization: sessionStorage.getItem('token'),
      location: this.translateService.currentLang
    };
    const params = { ...filter, ...extraParams };
    const query = this.router.createUrlTree([], { queryParams: params });
    return this.appConfig.api + 'users-worksheet/' + this.appConfig.apiPartnershipId + '?' + (this.serializer.serialize(query).split('?')[1] || '');
  }

  getAdhesionRequests(filter?: BackofficeAdhesionRequestsRequestModel): Observable<BackofficeAdhesionRequestResponseModel> {
    const httpParams: HttpParams = new HttpParams({
      fromObject: filter as any
    });
    return this.http.get<any>(this.appConfig.api + 'backoffice/users/' + this.appConfig.apiPartnershipId + '/registerRequests', { params: httpParams }).pipe(
      map((response) => {
        return {
          registerRequests: response.body.registerRequests,
          pagination: response.body.pagination
        };
      })
    );
  }
}
